<script setup>
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { useForm } from 'vee-validate';
import Button from 'primevue/button';
import RadioButton from 'primevue/radiobutton';

import { storeToRefs } from 'pinia';
import * as yup from '@/helpers/yup-distilled';

import { router, getNextRouteFromNewSigninState } from '@/helpers';
import { getFriendlyErrorMessage } from '@/helpers';
import { useAuthStore } from '@/stores';
import { useUserPreferenceStore } from '@/stores';

const route = useRoute();
const authStore = useAuthStore();
const userPreferenceStore = useUserPreferenceStore();

// redirect to login if no auth token
if (!authStore.authToken) {
	router.push(import.meta.env.VITE_SIGNIN_BASEURL);
}

const schema = yup.object().shape({
	mfaMethod: yup
		.string()
		.required('Please specify how you would like to receive a passcode.')
});

const { defineField, errors, handleSubmit, isSubmitting } = useForm({
	validationSchema: schema
});

const onSubmit = handleSubmit((values, { setErrors }) => {
	const { mfaMethod } = values;

	userPreferenceStore.setUserPreference('favoriteMfaMethod', mfaMethod);
	return authStore
		.requestPasscode(mfaMethod)
		.then(() => {
			router.replace(
				getNextRouteFromNewSigninState(authStore.signInState, route)
			);
		})
		.catch(async (error) => {
			const errorOutput = await getFriendlyErrorMessage(error);
			setErrors({ apiError: errorOutput.message });
		});
});

const mfaMethodDescription = (method) => {
	switch (method) {
		case 'email':
			return `Send an email to ${mfaMethods.value[method].emailAddress}`;
		case 'sms':
			return `Send a text message to ${mfaMethods.value[method].phoneNumber}`;
	}
};

const setDefault = () => {
	let defaultMethod;
	const { favoriteMfaMethod } = userPreferenceStore.userPreferences || {};
	if (favoriteMfaMethod && mfaMethods.value[favoriteMfaMethod]) {
		defaultMethod = favoriteMfaMethod;
	} else {
		defaultMethod = mfaMethods.value.email ? 'email' : 'sms'; //arbitrarily prefer email
	}
	mfaMethod.value = defaultMethod;
};

const [mfaMethod] = defineField('mfaMethod');
const { mfaMethods } = storeToRefs(authStore);
const noMethodsAvailable = ref(Object.keys(mfaMethods).length == 0);

if (!mfaMethods.value) {
	router.push(import.meta.env.VITE_SIGNIN_BASEURL);
} else {
	setDefault();
}
</script>

<template>
	<form v-if="mfaMethods" @submit="onSubmit">
		<div class="p-fluid login-small-form">
			<div class="p-field">
				For your security, we will send you a one-time passcode.
			</div>
			<div class="p-field mt-1 font-semibold">
				How would you like to receive it?
			</div>
			<div class="p-field mt-2" v-if="noMethodsAvailable">
				No MFA methods are registered to your login. Please contact your
				administrator.
			</div>
			<div
				class="p-field mt-2 radio-row"
				:key="method"
				v-for="method in Object.keys(mfaMethods)"
				v-else
			>
				<RadioButton
					class="mr-1"
					variant="filled"
					v-model="mfaMethod"
					name="mfaMethod"
					:value="method"
				/>
				<label for="mfaMethod">{{
					mfaMethodDescription(method)
				}}</label>
			</div>
			<div v-if="errors.mfaMethod" class="p-error">
				{{ errors.mfaMethod }}&nbsp;
			</div>
		</div>
		<div class="p-fluid">
			<Button
				:disabled="isSubmitting || noMethodsAvailable"
				label="Submit"
				type="submit"
			></Button>
			<div v-if="errors.apiError" class="p-error">
				{{ errors.apiError }}
			</div>
		</div>
	</form>
</template>

<style>
.radio-row {
	display: flex;
	flex-direction: row;
	align-items: center;
}
</style>
